import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import { withRouter, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import qs from 'query-string';

import { AlbumForWebGalleries } from '../../graphql/queries/album';
import { AlbumFolderForWebGalleries } from '../../graphql/queries/album-folder';
import FullScreenLoader from '../../../../components/global/FullScreenLoader';
import NotFound from '../../../pro/views/NotFound';
import { isAlbumFolder } from '../../../../util/album-folder';
import {
  getWebGalleryEntityPath,
  requiresAuthentication,
  webGalleryIsInviteOnly,
} from '../../utilities/web-galleries';
import ErrorView from '../../../../components/global/ErrorView';
import { openModal as openModalAction } from '../../../../actions/modal-actions';
import { WEB_GALLERY_PRO_ONBOARDING_MODAL } from '../WebGalleryProOnboardingInterstitial';
import { ALL_PHOTOS_MODE } from '../../../../constants/album-folder';
import { ALBUM_SUBTYPE } from '../../../../constants/albums';
import WebGalleryAlbumFolderMeta from '../../containers/WebGalleryAlbumFolderMeta';

const propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
  }),
  loading: PropTypes.bool,
  openModal: PropTypes.func.isRequired,
  authenticated: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
};

const WebGalleryOnboardingRedirect = ({
  entity,
  loading,
  openModal,
  authenticated,
  location,
}) => {
  const albumFolder = isAlbumFolder(entity)
    ? entity
    : entity?.root_album_folder;

  const isOnboardable = entity?.joins_enabled || albumFolder?.joins_enabled;
  const isWebGallery = entity?.is_web_gallery;
  const isSchoolSubtype = albumFolder?.album_subtype === ALBUM_SUBTYPE.SCHOOLS;
  const isCampSubtype = albumFolder?.album_subtype === ALBUM_SUBTYPE.CAMPS;

  const isPrivateFaceblocked =
    !albumFolder?.joins_enabled &&
    albumFolder?.all_photos_mode === ALL_PHOTOS_MODE.PRIVATE;

  const isPublicWithJoinsEnabled =
    isOnboardable && albumFolder?.all_photos_mode === ALL_PHOTOS_MODE.PUBLIC;
  const meta = <WebGalleryAlbumFolderMeta albumFolder={albumFolder} />;

  useEffect(() => {
    if (
      albumFolder &&
      isOnboardable &&
      isWebGallery &&
      !isSchoolSubtype &&
      !isCampSubtype
    ) {
      // Open up R/L door:
      openModal(WEB_GALLERY_PRO_ONBOARDING_MODAL, {
        albumFolderId: albumFolder.id,
      });
    }
  }, [albumFolder, isOnboardable, isWebGallery]);

  if (!loading && !entity) {
    return (
      <>
        {meta}
        <NotFound />
      </>
    );
  }

  if (loading) {
    return (
      <>
        {meta}
        <FullScreenLoader />
      </>
    );
  }

  if (requiresAuthentication(entity, authenticated)) {
    if (isOnboardable) {
      const queryParams = location?.search ? `${location.search}` : '';
      return (
        <>
          {meta}
          <Redirect
            to={`/discover/album-folder/${albumFolder.id}/start${queryParams}`}
          />
        </>
      );
    }
    return (
      <>
        {meta}
        <Redirect
          to={{
            pathname: '/auth/phone',
            search: `?${qs.stringify({
              next: `${location.pathname}${location.search || ''}`,
            })}`,
          }}
        />
      </>
    );
  }

  if (isPublicWithJoinsEnabled) {
    return <Redirect to={`/s/all-photos-access/${albumFolder.id}`} />;
  }

  if (isOnboardable) {
    const queryParams = location?.search ? `${location.search}` : '';
    return (
      <>
        {meta}
        <Redirect
          to={`/discover/album-folder/${albumFolder.id}/start${queryParams}`}
        />
      </>
    );
  }

  if (isWebGallery && !webGalleryIsInviteOnly(entity)) {
    return (
      <>
        {meta}
        <Redirect to={getWebGalleryEntityPath(entity)} />
      </>
    );
  }

  if (isPrivateFaceblocked || isPublicWithJoinsEnabled) {
    return (
      <>
        {meta}
        <Redirect to={`/s/all-photos-access/${albumFolder.id}`} />
      </>
    );
  }

  // Safeguard against non-onboardable and non-wg entities:
  return (
    <>
      {meta}
      <ErrorView
        location={{
          state: {
            error:
              "It looks like something went wrong with the page you're trying to access. Please contact one of our administrators.",
          },
        }}
      />
    </>
  );
};

WebGalleryOnboardingRedirect.propTypes = propTypes;

const withAlbumForWebGalleries = graphql(AlbumForWebGalleries, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumId,
    },
    // In order for openModal to work properly,
    // ssr needs to be disabled.
    ssr: false,
  }),
  props: ({ data: { loading, album } }) => ({
    loading,
    entity: album,
  }),
  skip: ({ match }) => !match.params.albumId,
});

const withAlbumFolderForWebGalleries = graphql(AlbumFolderForWebGalleries, {
  options: ({ match }) => ({
    variables: {
      id:
        // Replace the Old Queensland Oztag Gallery 2026 (PROD) to the recreated one.
        // Queensland deleted gallery: NqUfj9yTqcEwgD8uwv8gQSP
        // Queensland reworked gallery: PAP9wAjTZLq2MEodxLygn6V
        match.params.albumFolderId === 'NqUfj9yTqcEwgD8uwv8gQSP'
          ? 'PAP9wAjTZLq2MEodxLygn6V'
          : match.params.albumFolderId,
    },
    // In order for openModal to work properly,
    // ssr needs to be disabled.
    ssr: false,
  }),
  props: ({ data: { albumFolder, loading } }) => ({
    entity: albumFolder,
    loading,
  }),
  skip: ({ match }) => !match.params.albumFolderId,
});

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

const mapDispatchToProps = {
  openModal: openModalAction,
};

export { WebGalleryOnboardingRedirect as WebGalleryOnboardingRedirectComponent };

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withAlbumForWebGalleries,
  withAlbumFolderForWebGalleries,
)(WebGalleryOnboardingRedirect);
